import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { compressFormData } from "@/utils/imageCompressor";
import { beginLogout, getLogoutAbortSignal, isLogoutInProgress } from "@/utils/logoutGuard";
import { requestPushPermissionAndSubscribe, unsubscribePush, clearAppBadge } from "@/utils/pushClient";
import { clearReadCache } from "@/utils/readCache";
import { ActiveRoleContext, installActiveRoleHeader } from "@/utils/activeRole";
import ForcePasswordChange from "@/components/common/ForcePasswordChange";

installActiveRoleHeader();

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// CRITICAL FIX: Setup axios default header IMMEDIATELY on module load
// This ensures token is attached before any component renders
const initToken = localStorage.getItem("token");
if (initToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${initToken}`;
}

// ============ Oct 2026 — Weak-signal resilience (Fase 1) ============
// Masalah: sinyal lemah (timeout / tanpa respons / 5xx) diperlakukan sama
// seperti "login tidak valid", sehingga token dihapus dan user terlempar ke
// halaman login. Helper di bawah membedakan masalah jaringan SEMENTARA dari
// penolakan server yang sebenarnya (401/403/404 dst).
const AUTH_ME_TIMEOUT_MS = 10000;
const AUTH_REFRESH_TIMEOUT_MS = 20000;
const LOGIN_TIMEOUT_MS = 25000;
const LOGIN_RETRY_DELAY_MS = 1500;
const AUTH_ME_RETRY_DELAY_MS = 1500;
const CACHED_USER_KEY = "cached_user";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// True hanya untuk error axios tanpa respons (sinyal putus / timeout) atau
// respons server sementara (408/429/5xx). Request yang dibatalkan (logout) dan
// error buatan sendiri (mis. "No refresh token available") BUKAN transient.
const isTransientError = (error) => {
  if (!error) return false;
  if (axios.isCancel(error) || error.code === "ERR_CANCELED" || error.name === "AbortError") {
    return false;
  }
  if (!error.response) return !!error.isAxiosError;
  const status = error.response.status;
  return status === 408 || status === 429 || status >= 500;
};

// Salinan data user dipakai hanya kalau jaringan gagal saat aplikasi dibuka.
// Hanya dianggap valid kalau user_id-nya cocok dengan user di dalam token.
const readCachedUser = (token) => {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    if (!raw || !token) return null;
    const cached = JSON.parse(raw);
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(base64));
    if (cached && cached.user_id && cached.user_id === payload.user_id) {
      return cached;
    }
  } catch {
    /* salinan rusak: abaikan */
  }
  return null;
};

const writeCachedUser = (userData) => {
  try {
    if (userData) localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData));
  } catch {
    /* storage penuh / diblokir: abaikan */
  }
};

const clearCachedUser = () => {
  try {
    localStorage.removeItem(CACHED_USER_KEY);
  } catch {
    /* abaikan */
  }
  // Okt 2026: salinan data baca (daftar order dll) ikut dihapus saat logout / token dibuang.
  try { clearReadCache(); } catch { /* abaikan */ }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  const activeRole = useContext(ActiveRoleContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  // Okt 2026 — Mode peran aktif: di dalam ActiveRoleBoundary, user terlihat sebagai role itu.
  return useMemo(() => {
    if (!activeRole || !context.user) return context;
    return {
      ...context,
      user: { ...context.user, role: activeRole, primary_role: context.user.role, active_role: activeRole },
    };
  }, [context, activeRole]);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initializing = useRef(false);
  const initialized = useRef(false);
  // Okt 2026 — Password yang baru diketik di halaman login. Disimpan di memori
  // HANYA kalau server menandai akun wajib ganti password, supaya layar ganti
  // password tidak meminta password lama lagi. Tidak pernah ditulis ke storage.
  const loginPasswordRef = useRef(null);

  // Feb 2026 — Impersonate feature state. Session asli disimpan di localStorage
  // dengan prefix `original_*` supaya bisa direstore tanpa login ulang.
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = localStorage.getItem("impersonation_state");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  
  // P2 - JWT Refresh Token: Refs for refresh logic
  const isRefreshing = useRef(false);
  const refreshPromise = useRef(null);
  const failedQueue = useRef([]);

  // P2 - Process failed queue after refresh
  const processQueue = (error, token = null) => {
    failedQueue.current.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue.current = [];
  };

  // P2 - Refresh token function
  const refreshToken = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem("refresh_token");
    const tokenAtStart = localStorage.getItem("token");

    if (!storedRefreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      // Call refresh endpoint
      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: storedRefreshToken
      }, {
        // Skip interceptor for this request
        _skipAuthRefresh: true,
        // Tanpa timeout, request yang menggantung membuat isRefreshing terkunci
        // dan semua request lain ikut menunggu tanpa ujung.
        timeout: AUTH_REFRESH_TIMEOUT_MS
      });

      const { token, refresh_token } = response.data;

      // Store new tokens
      localStorage.setItem("token", token);
      if (refresh_token) {
        localStorage.setItem("refresh_token", refresh_token);
      }

      // Update axios default header
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      return token;
    } catch (error) {
      // Oct 2026 — Sinyal putus / timeout / 5xx BUKAN berarti refresh token
      // ditolak. Jangan hapus token; biarkan pemanggil mencoba lagi nanti.
      if (isTransientError(error)) {
        throw error;
      }
      // Race-condition guard: backend rotates refresh tokens on use, so if two
      // refresh calls ever race, the loser gets rejected here with an
      // already-revoked refresh_token. If another refresh already succeeded
      // and wrote a NEW token while this call was in flight, localStorage's
      // token will differ from what we started with — in that case, do NOT
      // wipe it; the winner's token is valid and should be kept.
      const tokenNow = localStorage.getItem("token");
      const anotherRefreshAlreadySucceeded = tokenNow && tokenNow !== tokenAtStart;
      if (!anotherRefreshAlreadySucceeded) {
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        delete axios.defaults.headers.common["Authorization"];
      }
      throw error;
    }
  }, []);

  // P2 - Force logout function
  const forceLogout = useCallback(() => {
    // Start silent-error window so any in-flight 401s don't toast.
    beginLogout();
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    clearCachedUser();
    loginPasswordRef.current = null;
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);

    // Redirect to login (silent - no popup)
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }, []);

  useEffect(() => {
    // CRITICAL FIX: Prevent any re-initialization
    if (initialized.current || initializing.current) {
      return;
    }
    
    initializing.current = true;
    
    // P2 - Setup axios request interceptor
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        // Skip auth header for file serving endpoints
        const isFileEndpoint = config.url?.includes('/logistic-returns/files/') || 
                              config.url?.includes('/supply-requests/documents/');
        
        if (!isFileEndpoint) {
          const token = localStorage.getItem("token");
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Attach shared logout-abort signal so we can cancel all in-flight
        // requests on logout. Skip if caller already provided their own signal.
        if (!config.signal) {
          config.signal = getLogoutAbortSignal();
        }
        
        // Global image compression — transparently shrink any image File > 1MB
        // contained in FormData payloads before they go to the network. This
        // covers every axios upload across all roles (admin, nakes, marketing,
        // gudang, finance) without per-call wiring.
        if (config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
          try {
            config.data = await compressFormData(config.data);
          } catch (e) {
            // Never block a request because compression failed.
            if (typeof console !== 'undefined') console.warn('[axios.compress] skipped:', e?.message);
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // P2 - Setup response interceptor with auto-refresh
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Skip for certain endpoints
        const skipEndpoints = [
          '/auth/login',
          '/auth/refresh',
          '/auth/logout',
          '/logistic-returns/files/',
          '/supply-requests/documents/'
        ];
        
        const shouldSkip = skipEndpoints.some(ep => originalRequest?.url?.includes(ep)) ||
                          originalRequest?._skipAuthRefresh ||
                          originalRequest?._retry;
        
        // P2 - Handle 401 with auto-refresh
        if (error.response?.status === 401 && !shouldSkip) {
          // Feb 2026 (Bug A fix) — Skip auto-refresh entirely while
          // impersonating. Impersonation session has NO refresh_token
          // (deliberately cleared in startImpersonate to prevent refreshing
          // into the target's account). Without this guard, ANY 401 during
          // impersonate — including a routine POST /nakes-chats/.../messages
          // when the impersonation JWT expires — would trigger refreshToken()
          // → "No refresh token available" → forceLogout() → the ORIGINAL
          // admin is silently kicked out of their own session, and the toast
          // shows generic "Gagal kirim pesan" as the last visible artifact.
          // Under impersonation, let the 401 propagate to the caller so it
          // can render an informative error and the admin can either stop
          // impersonating or refresh manually.
          const _isImpersonating = !!localStorage.getItem('impersonation_state');
          if (_isImpersonating) {
            return Promise.reject(error);
          }

          // Check if error is due to expired/invalid token (not wrong credentials)
          const errorDetail = error.response?.data?.detail?.toLowerCase() || '';
          const isTokenIssue = errorDetail.includes('expired') ||
                              errorDetail.includes('session') ||
                              errorDetail.includes('invalid token') ||
                              errorDetail.includes('token');
          
          if (isTokenIssue) {
            // If already refreshing, queue this request
            if (isRefreshing.current) {
              return new Promise((resolve, reject) => {
                failedQueue.current.push({ resolve, reject });
              }).then(token => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axios(originalRequest);
              }).catch(err => {
                return Promise.reject(err);
              });
            }

            isRefreshing.current = true;
            originalRequest._retry = true;

            try {
              const newToken = await refreshToken();
              processQueue(null, newToken);
              
              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            } catch (refreshError) {
              processQueue(refreshError, null);
              // Silent logout on refresh failure, KECUALI kegagalannya karena
              // jaringan (Oct 2026): user tetap login, request ini saja yang gagal.
              if (!isTransientError(refreshError)) {
                forceLogout();
              }
              return Promise.reject(refreshError);
            } finally {
              isRefreshing.current = false;
            }
          } else {
            // Invalid credentials or other 401 - force logout
            forceLogout();
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    // Oct 2026 — Kalau jaringan gagal saat aplikasi dibuka, pakai salinan data
    // user yang tersimpan lalu validasi ulang di belakang layar. Token TIDAK
    // dihapus karena masalah jaringan.
    let revalidateTimer = null;
    let revalidateCancelled = false;
    const REVALIDATE_DELAYS_MS = [5000, 15000, 30000, 60000];

    const fetchMe = () => axios.get(`${API}/auth/me`, { timeout: AUTH_ME_TIMEOUT_MS });

    const scheduleRevalidate = (attempt = 0) => {
      if (revalidateCancelled || attempt >= REVALIDATE_DELAYS_MS.length) return;
      revalidateTimer = setTimeout(async () => {
        if (revalidateCancelled || !localStorage.getItem("token")) return;
        try {
          const response = await fetchMe();
          if (!revalidateCancelled) setUser(response.data);
        } catch (error) {
          // 401 ditangani interceptor (refresh atau logout). Ulangi hanya
          // kalau masih masalah jaringan.
          if (isTransientError(error)) scheduleRevalidate(attempt + 1);
        }
      }, REVALIDATE_DELAYS_MS[attempt]);
    };

    const restoreCachedUser = (token) => {
      const cached = readCachedUser(token);
      if (!cached) return false;
      setUser(cached);
      scheduleRevalidate();
      return true;
    };

    // Saat jaringan gagal: pakai salinan user; kalau belum ada, coba sekali
    // lagi sebelum menyerah. Token tetap disimpan supaya buka berikutnya
    // langsung masuk.
    const restoreOrRetry = async (token) => {
      if (restoreCachedUser(token)) return;
      try {
        await sleep(AUTH_ME_RETRY_DELAY_MS);
        const retryResponse = await fetchMe();
        setUser(retryResponse.data);
      } catch {
        setUser(null);
      }
    };

    const initAuth = async () => {
      try {
        const token = localStorage.getItem("token");

        if (token) {
          try {
            const response = await fetchMe();
            setUser(response.data);
            // iter391 — Re-subscribe Web Push on reload if permission already
            // granted (silent-fail; won't re-prompt if user denied).
            requestPushPermissionAndSubscribe(token).catch(() => {});
          } catch (error) {
            // P2 - Try refresh before giving up
            if (error.response?.status === 401) {
              try {
                await refreshToken();
                const response = await fetchMe();
                setUser(response.data);
              } catch (refreshError) {
                if (isTransientError(refreshError)) {
                  // Jaringan bermasalah, bukan login tidak valid: token tetap.
                  await restoreOrRetry(localStorage.getItem("token"));
                } else {
                  // Refresh ditolak server - clear tokens
                  localStorage.removeItem("token");
                  localStorage.removeItem("refresh_token");
                  clearCachedUser();
                  setUser(null);
                }
              }
            } else if (isTransientError(error)) {
              console.warn("Token validation skipped (network):", error.message);
              await restoreOrRetry(token);
            } else {
              console.error("Token validation failed:", error.message);
              localStorage.removeItem("token");
              clearCachedUser();
              setUser(null);
            }
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        setUser(null);
      } finally {
        setLoading(false);
        initialized.current = true;
        initializing.current = false;
      }
    };
    
    initAuth();
    
    // P2 ENHANCEMENT: Proactive token refresh - refresh 2 min before expiry
    // This prevents 401 errors by refreshing token before it expires
    const tokenRefreshInterval = setInterval(async () => {
      const token = localStorage.getItem("token");
      const refreshTokenValue = localStorage.getItem("refresh_token");
      
      if (token && refreshTokenValue && !isRefreshing.current) {
        let timeUntilExpiry;
        try {
          // Decode token to check expiry (without verification)
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(window.atob(base64));

          const expiryTime = payload.exp * 1000; // Convert to ms
          const now = Date.now();
          timeUntilExpiry = expiryTime - now;
        } catch (e) {
          // Malformed/undecodable token — nothing to schedule a refresh for.
          return;
        }

        // Refresh if less than 2 minutes until expiry
        if (timeUntilExpiry < 2 * 60 * 1000 && timeUntilExpiry > 0) {
          console.log('[Auth] Proactive token refresh - expiring soon');
          // Same lock as the reactive interceptor path — a 401 firing while
          // this is in flight will now queue via failedQueue instead of
          // kicking off a second, parallel /auth/refresh call with the same
          // refresh_token (which the backend's rotation would always reject).
          isRefreshing.current = true;
          try {
            const newToken = await refreshToken();
            processQueue(null, newToken);
          } catch (refreshError) {
            processQueue(refreshError, null);
            // Only force logout if there's genuinely no valid token left —
            // if a concurrent reactive refresh won the race, refreshToken()
            // already preserved its token and we're fine, just lost the race.
            if (!localStorage.getItem("token")) {
              console.warn('[Auth] Proactive token refresh failed and no valid token remains — logging out', refreshError);
              forceLogout();
            } else {
              console.log('[Auth] Proactive token refresh did not complete (network issue or lost a rotation race) — token kept, will retry');
            }
          } finally {
            isRefreshing.current = false;
          }
        }
      }
    }, 60 * 1000); // Check every minute
    
    // Cleanup interceptors and interval
    return () => {
      revalidateCancelled = true;
      if (revalidateTimer) clearTimeout(revalidateTimer);
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      clearInterval(tokenRefreshInterval);
    };
  }, [refreshToken, forceLogout]);

  // Oct 2026 — Simpan salinan data user setiap kali user berhasil dimuat
  // (login, /auth/me, refreshUser, impersonate). Dipakai hanya sebagai
  // cadangan saat jaringan gagal ketika aplikasi dibuka.
  useEffect(() => {
    // Hanya objek user lengkap dari server (selalu punya created_at). Snapshot
    // parsial (mis. fallback stopImpersonate) tidak boleh jadi cadangan.
    if (user && user.created_at) writeCachedUser(user);
  }, [user]);

  // Refresh user data from DB (single source of truth)
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      return null;
    }
  }, []);

  // Okt 2026 — Dipanggil layar wajib ganti password setelah berhasil. Flag
  // dimatikan lokal dulu (layar langsung tertutup), lalu disinkronkan dari server.
  const handlePasswordChanged = useCallback(async () => {
    loginPasswordRef.current = null;
    setUser((u) => (u ? { ...u, must_change_password: false } : u));
    await refreshUser();
  }, [refreshUser]);

  // Oct 2026 — Login dengan batas waktu. Kalau TIDAK ada respons sama sekali
  // (sinyal putus / timeout), coba sekali lagi. Error yang membawa respons
  // (password salah, akun nonaktif, 5xx) tidak diulang.
  const postLogin = async (user_id, password, onRetry) => {
    const config = { timeout: LOGIN_TIMEOUT_MS };
    try {
      return await axios.post(`${API}/auth/login`, { user_id, password }, config);
    } catch (error) {
      if (!isTransientError(error) || error.response) throw error;
      if (typeof onRetry === "function") {
        try { onRetry(); } catch { /* abaikan */ }
      }
      await sleep(LOGIN_RETRY_DELAY_MS);
      return axios.post(`${API}/auth/login`, { user_id, password }, config);
    }
  };

  const login = async (user_id, password, options = {}) => {
    try {
      const response = await postLogin(user_id, password, options.onRetry);
      const { token, refresh_token, user: userData } = response.data;
      
      // P2 - Store both tokens
      localStorage.setItem("token", token);
      if (refresh_token) {
        localStorage.setItem("refresh_token", refresh_token);
      }
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      loginPasswordRef.current = userData?.must_change_password ? password : null;
      setUser(userData);

      // iter391 — Lazily request notification permission & register Web Push
      // subscription. Silent-fail: any failure never blocks the login flow.
      requestPushPermissionAndSubscribe(token).catch(() => {});
      
      return userData;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  // Feb 2026 — Start impersonate session (Super Admin / Supervisor only).
  // Saves current session under `original_*` keys, replaces token with the
  // one issued for the target user. Session id disimpan agar bisa di-stop.
  const startImpersonate = useCallback(async (targetUserId) => {
    if (impersonation) {
      throw new Error("Sudah dalam sesi impersonate. Kembali ke akun asli dulu.");
    }
    const currentToken = localStorage.getItem("token");
    const currentRefresh = localStorage.getItem("refresh_token");
    if (!currentToken) throw new Error("Tidak ada sesi aktif");

    const response = await axios.post(`${API}/auth/impersonate/${targetUserId}`);
    const { token: newToken, user: targetUser, session_id, impersonator } = response.data;

    // Backup original session
    if (currentToken) localStorage.setItem("original_token", currentToken);
    if (currentRefresh) localStorage.setItem("original_refresh_token", currentRefresh);

    // Snapshot original user (from React state) so banner can label the actor
    const originalUserSnapshot = impersonator || (user ? {
      user_id: user.user_id,
      name: user.name,
      role: user.role,
    } : null);
    if (originalUserSnapshot) {
      localStorage.setItem("original_user", JSON.stringify(originalUserSnapshot));
    }

    // Swap in impersonation token
    localStorage.setItem("token", newToken);
    clearReadCache(); // Okt 2026: data akun asli tidak boleh bercampur dengan akun target
    // Impersonation session has no refresh token — clear it so proactive
    // refresh doesn't accidentally refresh into the target's account.
    localStorage.removeItem("refresh_token");
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

    const state = {
      session_id,
      original_user: originalUserSnapshot,
      target_user: targetUser,
      started_at: new Date().toISOString(),
    };
    localStorage.setItem("impersonation_state", JSON.stringify(state));
    setImpersonation(state);
    setUser(targetUser);
    return targetUser;
  }, [impersonation, user]);

  // Feb 2026 — Stop impersonate: call backend to close audit session then
  // restore original token/user without requiring re-login.
  const stopImpersonate = useCallback(async () => {
    if (!impersonation) return null;
    const sessionId = impersonation.session_id;

    // Call backend using the CURRENT (impersonation) token — backend logs will
    // pair start/stop by session_id.
    try {
      await axios.post(`${API}/auth/impersonate/stop`, { session_id: sessionId });
    } catch (e) {
      // Non-fatal: still restore locally so user isn't stuck.
      console.warn("[impersonate] stop endpoint failed, restoring locally:", e?.message);
    }

    const originalToken = localStorage.getItem("original_token");
    const originalRefresh = localStorage.getItem("original_refresh_token");
    const originalUserRaw = localStorage.getItem("original_user");

    // Clean impersonation state first
    localStorage.removeItem("impersonation_state");
    localStorage.removeItem("original_token");
    localStorage.removeItem("original_refresh_token");
    localStorage.removeItem("original_user");
    setImpersonation(null);
    // Okt 2026 — Langsung pakai snapshot admin asli supaya data akun target
    // (mis. flag wajib ganti password) tidak sempat tampil selagi /auth/me dimuat.
    try {
      const originalSnapshot = originalUserRaw ? JSON.parse(originalUserRaw) : null;
      if (originalSnapshot) setUser(originalSnapshot);
    } catch { /* snapshot rusak: abaikan, /auth/me di bawah yang menentukan */ }

    if (!originalToken) {
      // No original session to restore (edge case). Force fresh login.
      forceLogout();
      return null;
    }

    // Restore original session
    localStorage.setItem("token", originalToken);
    clearReadCache(); // Okt 2026: data akun target tidak boleh tersisa di HP
    if (originalRefresh) localStorage.setItem("refresh_token", originalRefresh);
    axios.defaults.headers.common["Authorization"] = `Bearer ${originalToken}`;

    // Refetch /auth/me with restored token so state matches server truth
    try {
      const meResp = await axios.get(`${API}/auth/me`);
      setUser(meResp.data);
      return meResp.data;
    } catch {
      // Fallback to cached original_user
      try {
        const cached = originalUserRaw ? JSON.parse(originalUserRaw) : null;
        if (cached) setUser(cached);
        return cached;
      } catch {
        return null;
      }
    }
  }, [impersonation, forceLogout]);

  const logout = async () => {
    // Begin guarded logout: aborts in-flight requests and starts the
    // silent-error window so dashboards won't show "Gagal memuat" toasts
    // from requests that fire concurrently with token clearing.
    beginLogout();
    // Feb 2026 — Kalau sedang impersonate, bersihkan state impersonate juga
    // supaya banner tidak nyangkut setelah logout.
    if (impersonation) {
      try {
        await axios.post(`${API}/auth/impersonate/stop`, {
          session_id: impersonation.session_id,
        }).catch(() => {});
      } catch { /* ignore */ }
      localStorage.removeItem("impersonation_state");
      localStorage.removeItem("original_token");
      localStorage.removeItem("original_refresh_token");
      localStorage.removeItem("original_user");
      setImpersonation(null);
    }
    try {
      const refreshTokenValue = localStorage.getItem("refresh_token");
      const currentToken = localStorage.getItem("token");
      
      // iter391 — Unsubscribe Web Push before we drop the token. Silent-fail.
      if (currentToken) {
        await unsubscribePush(currentToken).catch(() => {});
      }
      // Clear app icon badge on logout.
      clearAppBadge();
      
      // P2 - Call logout endpoint to revoke refresh token
      // Use a dedicated AbortController for this so the global abort above
      // doesn't cancel our own logout call.
      await axios.post(`${API}/auth/logout`, {
        refresh_token: refreshTokenValue
      }, { signal: new AbortController().signal }).catch(() => {}); // Ignore errors - we're logging out anyway
      
    } finally {
      // Always clear local state
      clearCachedUser();
      loginPasswordRef.current = null;
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      refreshToken,
      refreshUser,
      // Impersonate
      isImpersonating: !!impersonation,
      impersonation,
      originalUser: impersonation?.original_user || null,
      startImpersonate,
      stopImpersonate,
    }}>
      {/* Okt 2026 — Akun yang login dengan password universal lama wajib ganti
          password dulu. Tidak berlaku saat admin sedang impersonate. */}
      {user?.must_change_password && !impersonation ? (
        <ForcePasswordChange
          user={user}
          knownOldPassword={loginPasswordRef.current}
          onDone={handlePasswordChanged}
          onLogout={logout}
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
